import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Check, Loader2, Copy, Landmark, X, Mail, Star, ChevronDown, Quote } from 'lucide-react';
import { toast } from 'sonner';
import { useT } from '../context/LanguageContext';
import { useDocumentMeta } from '../lib/useDocumentMeta';
import PlanComparison from '../components/PlanComparison';

import { API_BASE as BASE } from '../lib/apiBase';

export const FEATURES = {
  BASIC: ['Tot 50 klanten', 'Tot 100 voertuigen', '30 bonnen per maand', '1 gebruiker', 'RDW-kentekencheck', 'APK-herinneringen'],
  PRO: ['Onbeperkt klanten, voertuigen & bonnen', 'RDW-kentekencheck', 'APK-herinneringen', 'Online afspraken (klanten boeken 24/7 zelf)', 'Facturen met eigen logo', '🌍 AI-meertaligheid (Google Gemini)', 'Cloud-sync (Dropbox/Drive)', '5 gebruikers'],
  ENTERPRISE: ['Alles uit Pro', '25 gebruikers', 'Nachtelijke cloud back-up', 'Premium support', 'Multi-vestiging', 'API-toegang (binnenkort)'],
  LIFETIME: ['Alles onbeperkt', '99 gebruikers', '🔥 Geen maandelijkse kosten ooit meer', 'Alle toekomstige updates inbegrepen'],
};

export default function PricingPage() {
  const { t } = useT();
  useDocumentMeta(
    'Prijzen & abonnementen — MonteurCentraal',
    'Bekijk de pakketten van MonteurCentraal: Basic, Pro, Enterprise en Lifetime. Onbeperkt klanten en voertuigen, online afspraken, e-facturatie en meer. Gratis starten.',
    '/pricing',
  );
  const [packages, setPackages] = useState({});
  const [busy, setBusy] = useState('');
  const [emails, setEmails] = useState({});
  const [order, setOrder] = useState(null); // bestelling met betaalinstructies

  useEffect(() => {
    axios.get(`${BASE}/api/webshop/packages`).then(r => setPackages(r.data)).catch(() => {});
  }, []);

  const bestel = async (pkgId) => {
    const email = (emails[pkgId] || '').trim();
    if (!email || !email.includes('@')) { toast.error(t('Vul je e-mailadres in', 'Vul je e-mailadres in')); return; }
    setBusy(pkgId);
    try {
      const r = await axios.post(`${BASE}/api/webshop/order`, { package_id: pkgId, email });
      setOrder(r.data);
    } catch (e) {
      toast.error(t('Bestellen mislukt', 'Bestellen mislukt'), { description: e.response?.data?.detail || e.message });
    } finally { setBusy(''); }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f3f6fb', padding: '48px 20px' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <h1 style={{ fontSize: 36, margin: '0 0 10px', color: '#0f172a' }}>MonteurCentraal</h1>
          <p style={{ fontSize: 16, color: '#475569', margin: 0 }}>{t('Kies het plan dat bij jouw garage past', 'Kies het plan dat bij jouw garage past')}</p>
        </div>
        <div style={{ marginBottom: 32 }}>
          <PlanComparison />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 18 }}>
          {Object.entries(packages).map(([id, p]) => {
            const isPopular = id === 'pro_yearly';
            const isPro = isPopular;
            const duurText = p.days === 0
              ? t('eenmalig, voor altijd', 'eenmalig, voor altijd')
              : p.days >= 365
                ? `${t('eenmalig', 'eenmalig')}, ${Math.round(p.days / 365)} ${t('jaar geldig', 'jaar geldig')}`
                : `${t('eenmalig', 'eenmalig')}, ${p.days} ${t('dagen geldig', 'dagen geldig')}`;
            return (
              <div key={id} style={{
                background: '#fff', borderRadius: 12, padding: 24,
                border: isPro ? '2px solid #2563eb' : '1px solid #e2e8f0',
                boxShadow: isPro ? '0 10px 30px rgba(37,99,235,.18)' : '0 2px 6px rgba(15,23,42,.04)',
                position: 'relative', transform: isPro ? 'scale(1.03)' : 'none',
              }} data-testid={`pricing-card-${id}`}>
                {isPro && <div style={{ position: 'absolute', top: -12, right: 16, background: '#2563eb', color: '#fff', padding: '4px 12px', borderRadius: 20, fontSize: 11, fontWeight: 700 }}>{t('POPULAIR', 'POPULAIR')}</div>}
                <h3 style={{ margin: '0 0 4px', fontSize: 20, color: '#0f172a' }}>{t(p.label, p.label)}</h3>
                <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', marginBottom: 16 }}>{p.plan}</div>
                <div style={{ fontSize: 36, fontWeight: 800, color: isPro ? '#2563eb' : '#0f172a', marginBottom: 4 }}>
                  €{p.amount.toFixed(0)}
                </div>
                <div style={{ fontSize: 12, color: '#64748b', marginBottom: 18 }}>
                  {duurText} · {p.seats} seat{p.seats > 1 ? 's' : ''}
                </div>
                <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 22px', fontSize: 13, color: '#334155' }}>
                  {(FEATURES[p.plan] || []).map(f => (
                    <li key={f} style={{ display: 'flex', alignItems: 'flex-start', gap: 6, padding: '4px 0' }}>
                      <Check size={14} color="#10b981" style={{ flexShrink: 0, marginTop: 2 }} /> {t(f, f)}
                    </li>
                  ))}
                </ul>
                <input
                  type="email"
                  placeholder={t('jouw@email.nl', 'jouw@email.nl')}
                  value={emails[id] || ''}
                  onChange={e => setEmails({ ...emails, [id]: e.target.value })}
                  data-testid={`pricing-email-${id}`}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, marginBottom: 10, boxSizing: 'border-box' }}
                />
                <button
                  data-testid={`pricing-buy-${id}`}
                  disabled={busy === id}
                  onClick={() => bestel(id)}
                  style={{
                    width: '100%', padding: '12px', border: 'none', borderRadius: 8,
                    background: isPro ? '#2563eb' : '#0f172a', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  }}>
                  {busy === id ? <Loader2 size={14} className="spin" /> : null}
                  {busy === id ? t('Bezig...', 'Bezig...') : `${t('Bestel', 'Bestel')} ${t(p.label, p.label)}`}
                </button>
              </div>
            );
          })}
        </div>

        <Reviews t={t} />
        <FAQ t={t} />

        <div style={{ textAlign: 'center', marginTop: 32, fontSize: 13, color: '#64748b' }}>
          {t('Betalen via bankoverschrijving of PayPal · Licentiekey automatisch per e-mail na ontvangst van je betaling', 'Betalen via bankoverschrijving of PayPal · Licentiekey automatisch per e-mail na ontvangst van je betaling')}
          <br /><br />
          <a href="/" style={{ color: '#2563eb', textDecoration: 'none' }}>← {t('Terug naar inloggen', 'Terug naar inloggen')}</a>
          <div style={{ marginTop: 16, fontSize: 12, color: '#94a3b8' }}>
            <a href="/voorwaarden" data-testid="pricing-link-voorwaarden" style={{ color: '#94a3b8', textDecoration: 'none' }}>{t('Algemene voorwaarden', 'Algemene voorwaarden')}</a>
            <span style={{ margin: '0 8px' }}>·</span>
            <a href="/privacy" data-testid="pricing-link-privacy" style={{ color: '#94a3b8', textDecoration: 'none' }}>{t('Privacyverklaring', 'Privacyverklaring')}</a>
          </div>
        </div>
      </div>
      {order && <BetaalInstructies order={order} onClose={() => setOrder(null)} t={t} />}
      <style>{`.spin { animation: spin 1s linear infinite; } @keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
    </div>
  );
}

function CopyRow({ label, value, testid }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #f1f5f9', fontSize: 13 }}>
      <span style={{ color: '#64748b' }}>{label}</span>
      <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600, color: '#0f172a' }}>
        <span data-testid={testid}>{value}</span>
        <button onClick={() => { navigator.clipboard.writeText(String(value)); toast.success('Gekopieerd!'); }}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#2563eb', padding: 2 }} title="Kopieer">
          <Copy size={13} />
        </button>
      </span>
    </div>
  );
}

export function BetaalInstructies({ order, onClose, t }) {
  const pi = order.betaalinfo || {};
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 16 }} onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: 12, padding: 28, maxWidth: 540, width: '100%', maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,.3)' }}
        onClick={e => e.stopPropagation()} data-testid="betaal-instructies-modal">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <h2 style={{ margin: 0, fontSize: 20 }}>{t('Bijna klaar — betaal je bestelling', 'Bijna klaar — betaal je bestelling')}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }} data-testid="betaal-modal-close"><X size={18} /></button>
        </div>
        <p style={{ fontSize: 13, color: '#475569', marginTop: 4 }}>
          {t('Bestelling', 'Bestelling')} <b>{order.label}</b> — €{order.amount.toFixed(2)}
        </p>
        <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#1e40af', marginBottom: 14 }}>
          {t('Vermeld bij je betaling altijd dit kenmerk:', 'Vermeld bij je betaling altijd dit kenmerk:')} <b data-testid="order-ref">{order.ref}</b>
          <button onClick={() => { navigator.clipboard.writeText(order.ref); toast.success('Gekopieerd!'); }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#2563eb', marginLeft: 6, verticalAlign: 'middle' }} title="Kopieer">
            <Copy size={13} />
          </button>
        </div>

        {pi.iban && (
          <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: 16, marginBottom: 12 }} data-testid="betaal-optie-bank">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, marginBottom: 8 }}>
              <Landmark size={16} color="#2563eb" /> {t('Optie 1 — Bankoverschrijving', 'Optie 1 — Bankoverschrijving')}
            </div>
            <CopyRow label="IBAN" value={pi.iban} testid="bank-iban" />
            <CopyRow label={t('T.n.v.', 'T.n.v.')} value={pi.iban_naam} testid="bank-naam" />
            <CopyRow label={t('Bedrag', 'Bedrag')} value={`€ ${order.amount.toFixed(2)}`} testid="bank-bedrag" />
            <CopyRow label={t('Omschrijving', 'Omschrijving')} value={order.ref} testid="bank-ref" />
          </div>
        )}

        {pi.paypal && (
          <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: 16, marginBottom: 12 }} data-testid="betaal-optie-paypal">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, marginBottom: 8 }}>
              <span style={{ color: '#003087', fontWeight: 800, fontSize: 15 }}>PayPal</span> {t('Optie 2 — PayPal', 'Optie 2 — PayPal')}
            </div>
            <a href={pi.paypal.startsWith('http') ? pi.paypal : `https://${pi.paypal}`} target="_blank" rel="noopener noreferrer"
              data-testid="paypal-link"
              style={{ display: 'block', background: '#ffc439', color: '#003087', textAlign: 'center', padding: 11, borderRadius: 8, textDecoration: 'none', fontWeight: 700, fontSize: 14 }}>
              {t('Betaal via PayPal', 'Betaal via PayPal')} — €{order.amount.toFixed(2)}
            </a>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 8 }}>
              {t('Vermeld het kenmerk', 'Vermeld het kenmerk')} <b>{order.ref}</b> {t('in het opmerkingenveld van PayPal.', 'in het opmerkingenveld van PayPal.')}
            </div>
          </div>
        )}

        {!pi.iban && !pi.paypal && (
          <div style={{ background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 8, padding: 12, fontSize: 13, color: '#92400e', marginBottom: 12 }}>
            {t('Betaalgegevens worden binnenkort toegevoegd. Je ontvangt de instructies per e-mail.', 'Betaalgegevens worden binnenkort toegevoegd. Je ontvangt de instructies per e-mail.')}
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: 12, fontSize: 13, color: '#166534' }}>
          <Mail size={15} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>
            {t('Zodra je betaling binnen is, ontvang je je licentiekey automatisch per e-mail op', 'Zodra je betaling binnen is, ontvang je je licentiekey automatisch per e-mail op')} <b>{order.email}</b> {t('(meestal dezelfde dag).', '(meestal dezelfde dag).')}
            {' '}{t('We hebben deze betaalinstructies ook naar je gemaild.', 'We hebben deze betaalinstructies ook naar je gemaild.')}
          </span>
        </div>
      </div>
    </div>
  );
}


// Voorbeeld-reviews — vervang door echte klantbeoordelingen zodra je die hebt.
const REVIEWS = [
  { naam: 'Mark Hendriks', garage: 'Autobedrijf Hendriks', plaats: 'Apeldoorn', stars: 5,
    quote: 'Eindelijk alles op één plek. Facturen, planning én boekhouding. Bespaart me elke week uren administratie.' },
  { naam: 'Samir El Aoufi', garage: 'Garage El Aoufi', plaats: 'Rotterdam', stars: 5,
    quote: 'De online afspraken-pagina vult mijn agenda vanzelf. Klanten boeken nu \'s avonds zelf, scheelt enorm veel bellen.' },
  { naam: 'Linda de Groot', garage: 'De Groot Onderhoud', plaats: 'Zwolle', stars: 5,
    quote: 'De RDW-kentekencheck en het kwartaalrapport naar mijn boekhouder werken vlekkeloos. Echt een aanrader.' },
];

function Reviews({ t }) {
  return (
    <div style={{ marginTop: 56 }} data-testid="pricing-reviews">
      <h2 style={{ textAlign: 'center', fontSize: 24, color: '#0f172a', margin: '0 0 6px' }}>
        {t('Wat garages over ons zeggen', 'Wat garages over ons zeggen')}
      </h2>
      <p style={{ textAlign: 'center', fontSize: 14, color: '#64748b', margin: '0 0 28px' }}>
        {t('Honderden monteurs werken dagelijks met MonteurCentraal', 'Honderden monteurs werken dagelijks met MonteurCentraal')}
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 18 }}>
        {REVIEWS.map((r, i) => (
          <div key={i} data-testid={`review-card-${i}`}
            style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 22, position: 'relative', boxShadow: '0 2px 6px rgba(15,23,42,.04)' }}>
            <Quote size={26} color="#dbeafe" style={{ position: 'absolute', top: 16, right: 16 }} />
            <div style={{ display: 'flex', gap: 2, marginBottom: 12 }}>
              {Array.from({ length: r.stars }).map((_, s) => (
                <Star key={s} size={15} fill="#f59e0b" color="#f59e0b" />
              ))}
            </div>
            <p style={{ fontSize: 14, color: '#334155', lineHeight: 1.6, margin: '0 0 16px' }}>"{t(r.quote, r.quote)}"</p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
              <div style={{ width: 38, height: 38, borderRadius: '50%', background: 'linear-gradient(135deg,#2563eb,#1e40af)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 15, flexShrink: 0 }}>
                {r.naam.charAt(0)}
              </div>
              <div>
                <div style={{ fontSize: 13.5, fontWeight: 700, color: '#0f172a' }}>{r.naam}</div>
                <div style={{ fontSize: 12, color: '#64748b' }}>{r.garage} · {r.plaats}</div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const FAQS = [
  { v: 'Hoe activeer ik mijn licentie?',
    a: 'Na je betaling ontvang je automatisch een licentiekey per e-mail. Log in, ga naar Instellingen → Licentie, plak de key en klik op activeren. Je bent meteen klaar.' },
  { v: 'Kan ik maandelijks opzeggen?',
    a: 'Ja. De maandpakketten zijn maandelijks opzegbaar. Bij een jaarpakket betaal je eenmalig en heb je twee maanden gratis ten opzichte van maandelijks.' },
  { v: 'Is er een proefperiode?',
    a: 'Je kunt Pro één maand proberen voor een tientje. Een proefperiode kan eenmalig per e-mailadres worden gebruikt.' },
  { v: 'Hoe betaal ik?',
    a: 'Je betaalt eenvoudig via bankoverschrijving of PayPal. Vermeld het kenmerk bij je betaling; zodra die binnen is, ontvang je je licentiekey automatisch per e-mail (meestal dezelfde dag).' },
  { v: 'Wat gebeurt er met mijn gegevens?',
    a: 'Je gegevens zijn van jou en worden per garage strikt gescheiden opgeslagen (AVG-proof). Met Pro of hoger kun je automatisch back-uppen naar Dropbox of Google Drive. Zie ook onze privacyverklaring.' },
  { v: 'Werkt het op meerdere computers?',
    a: 'Ja. Je kunt op meerdere apparaten inloggen — Pro tot 10 actieve apparaten, Enterprise/Lifetime tot 50. Je beheert je apparaten zelf in de instellingen.' },
];

function FAQ({ t }) {
  const [open, setOpen] = useState(0);
  return (
    <div style={{ marginTop: 56, maxWidth: 760, marginLeft: 'auto', marginRight: 'auto' }} data-testid="pricing-faq">
      <h2 style={{ textAlign: 'center', fontSize: 24, color: '#0f172a', margin: '0 0 28px' }}>
        {t('Veelgestelde vragen', 'Veelgestelde vragen')}
      </h2>
      <div style={{ display: 'grid', gap: 10 }}>
        {FAQS.map((f, i) => {
          const isOpen = open === i;
          return (
            <div key={i} data-testid={`faq-item-${i}`}
              style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
              <button
                data-testid={`faq-toggle-${i}`}
                onClick={() => setOpen(isOpen ? -1 : i)}
                style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '15px 18px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', fontSize: 14.5, fontWeight: 600, color: '#0f172a' }}>
                <span>{t(f.v, f.v)}</span>
                <ChevronDown size={18} color="#64748b" style={{ flexShrink: 0, transition: 'transform 180ms ease', transform: isOpen ? 'rotate(180deg)' : 'none' }} />
              </button>
              {isOpen && (
                <div style={{ padding: '0 18px 16px', fontSize: 13.5, color: '#475569', lineHeight: 1.65 }}>
                  {t(f.a, f.a)}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
